import ReactFacebookPixel from '@bettercart/react-facebook-pixel';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Payment, ProductData } from '../types';
import { TrackingPixels, FacebookPixel } from '../types/pixels';
import { calculatePixelValue } from '@/utils/payment';
import { resolveFacebookConfig } from '@/utils/pixels';

const useFacebookPixels = (pixelEventId?: string, activeIndex: number = 0) => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const offer = queryClient.getQueryData(['checkout', id]) as ProductData;
  const trackingPixels = (offer?.tracking_pixels as TrackingPixels) || ({} as TrackingPixels);

  const fbItem: FacebookPixel | undefined =
    Array.isArray(trackingPixels.facebook_pixels) && trackingPixels.facebook_pixels.length > 0
      ? trackingPixels.facebook_pixels[Math.min(activeIndex, trackingPixels.facebook_pixels.length - 1)]
      : undefined;

  const {
    fbBoletoPurchaseTrigger,
    fbBoletoConversionValue,
    fbPixPurchaseTrigger,
    fbPixConversionValue,
    fbPicpayPurchaseTrigger,
    fbPicpayConversionValue,
    fbNubankPurchaseTrigger,
    fbNubankConversionValue,
  } = resolveFacebookConfig(trackingPixels, fbItem);

  const getPaymentPayload = (payment: Payment) => {
    const disabled_orderbumps = offer?.disable_orderbump_pixel_events || false;
    let orders = Array.isArray(payment?.orders) ? payment.orders : [];
    if (disabled_orderbumps) {
      orders = orders.filter((o) => o?.offer_type !== 'orderbump');
    }
    const contentIds = orders.map((o) => o?.offerId).filter(Boolean);
    const n = Number(payment?.amount);
    const value = !disabled_orderbumps
      ? n
      : orders.reduce((acc, o) => acc + Number(o?.amount || 0), 0);
    return { contentIds, value };
  };

  const onCheckoutVisit = (offerData: ProductData) => {
    ReactFacebookPixel.track(
      'InitiateCheckout',
      {
        content_ids: [offerData.id],
        value: Number(offerData.price),
        currency: 'BRL',
      },
      {
        eventID: pixelEventId,
      }
    );
  };

  const onTicketGenerated = (payment: Payment) => {
    const { contentIds, value } = getPaymentPayload(payment);
    if (!contentIds.length) return;

    if (fbBoletoPurchaseTrigger) {
      return onPaymentApproved(payment, calculatePixelValue(value, fbBoletoConversionValue));
    }

    ReactFacebookPixel.trackCustom(
      'boleto_gerado',
      {
        content_ids: contentIds,
        value: Number(value),
        currency: 'BRL',
      },
      {
        eventID: pixelEventId,
      }
    );
  };

  const onPixGenerated = (payment: Payment) => {
    const { contentIds, value } = getPaymentPayload(payment);
    if (!contentIds.length) return;

    if (fbPixPurchaseTrigger) {
      return onPaymentApproved(payment, calculatePixelValue(value, fbPixConversionValue));
    }

    ReactFacebookPixel.trackCustom(
      'pix_gerado',
      {
        content_ids: contentIds,
        value: Number(value),
        currency: 'BRL',
      },
      {
        eventID: pixelEventId,
      }
    );
  };

  const onPicpayGenerated = (payment: Payment) => {
    const { contentIds, value } = getPaymentPayload(payment);
    if (!contentIds.length) return;

    if (fbPicpayPurchaseTrigger) {
      return onPaymentApproved(payment, calculatePixelValue(value, fbPicpayConversionValue));
    }

    ReactFacebookPixel.trackCustom(
      'picpay_gerado',
      {
        content_ids: contentIds,
        value: Number(value),
        currency: 'BRL',
      },
      {
        eventID: pixelEventId,
      }
    );
  };

  const onNubankGenerated = (payment: Payment) => {
    const { contentIds, value } = getPaymentPayload(payment);
    if (!contentIds.length) return;

    if (fbNubankPurchaseTrigger) {
      return onPaymentApproved(payment, calculatePixelValue(value, fbNubankConversionValue));
    }

    ReactFacebookPixel.trackCustom(
      'openfinance_nubank_gerado',
      {
        content_ids: contentIds,
        value: Number(value),
        currency: 'BRL',
      },
      {
        eventID: pixelEventId,
      }
    );
  };

  const onPaymentApproved = (payment: Payment, amount: number) => {
    const purchaseAlreadyTracked =
      (payment?.paymentMethod === 'boleto' && fbBoletoPurchaseTrigger) ||
      ((payment?.paymentMethod === 'pix' || payment?.paymentMethod === 'pix_auto') && fbPixPurchaseTrigger) ||
      (payment?.paymentMethod === 'picpay' && fbPicpayPurchaseTrigger) ||
      (payment?.paymentMethod === 'openfinance_nubank' && fbNubankPurchaseTrigger);

    const disabled_orderbumps = offer?.disable_orderbump_pixel_events || false;
    if (purchaseAlreadyTracked && payment?.status === 'paid') return;

    const { contentIds, value } = getPaymentPayload(payment);
    if (!contentIds.length) return;

    ReactFacebookPixel.track(
      'Purchase',
      {
        content_ids: contentIds,
        value: !disabled_orderbumps ? Number(amount) : Number(value),
        currency: 'BRL',
      },
      {
        eventID: payment?.id,
      }
    );

    ReactFacebookPixel.trackCustom(
      payment?.paymentMethod,
      {
        event_id: payment?.id,
        content_ids: contentIds,
        value: !disabled_orderbumps ? Number(amount) : Number(value),
        currency: 'BRL',
      },
      {
        eventID: payment?.id,
      }
    );
  };

  return {
    onCheckoutVisit,
    onTicketGenerated,
    onPixGenerated,
    onPicpayGenerated,
    onNubankGenerated,
    onPaymentApproved,
  };
};

export { useFacebookPixels };
